import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Receipt, Plus, Trash2, Calendar, TrendingUp, TrendingDown,
  Landmark, Package, Wallet, CheckCircle2, Download, AlertTriangle, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const fmtCurrency = (v) => `$${(Number(v) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short' });
};

const fmtFullDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' });
};

// AU BAS Lodgement Reminder Banner
function BasReminderBanner({ BACKEND_URL }) {
  const [reminder, setReminder] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/super-admin/gst/reminder`,
          { credentials: 'include' });
        if (res.ok && !cancelled) setReminder(await res.json());
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [BACKEND_URL]);

  if (!reminder || dismissed) return null;
  const { severity, days_until_due, due_date, quarter_label, running_outstanding, period_start, period_end } = reminder;
  if (severity === 'none') return null;

  const isOverdue = severity === 'overdue';
  const isUrgent = severity === 'urgent';
  const colorMap = {
    overdue: { bg: 'bg-red-50', border: 'border-red-400', text: 'text-red-900', sub: 'text-red-700', icon: 'text-red-600' },
    urgent:  { bg: 'bg-orange-50', border: 'border-orange-400', text: 'text-orange-900', sub: 'text-orange-700', icon: 'text-orange-600' },
    info:    { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-900', sub: 'text-blue-700', icon: 'text-blue-600' },
  };
  const c = colorMap[severity];
  const Icon = isOverdue || isUrgent ? AlertTriangle : Clock;
  const headline = isOverdue
    ? `BAS LODGEMENT OVERDUE — ${quarter_label} due ${fmtFullDate(due_date)}`
    : isUrgent
      ? `BAS due in ${days_until_due} day${days_until_due === 1 ? '' : 's'} — ${quarter_label} on ${fmtFullDate(due_date)}`
      : `BAS lodgement upcoming — ${quarter_label} due in ${days_until_due} days (${fmtFullDate(due_date)})`;

  return (
    <Card className={`p-4 border-l-4 ${c.border} ${c.bg}`} data-testid="bas-reminder-banner">
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${c.icon}`} />
        <div className="flex-1">
          <p className={`font-semibold ${c.text}`}>{headline}</p>
          <p className={`text-xs mt-1 ${c.sub}`}>
            Period {fmtDate(period_start)} → {fmtDate(period_end)}.{' '}
            <span className="font-medium">
              Running outstanding: ${(Number(running_outstanding) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
            </span>
            {running_outstanding < 0 && <span> (refund expected)</span>}
            {' '}— review the breakdown below and click <strong>Export BAS</strong> to generate the worksheet.
          </p>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className={`text-xs px-2 py-1 rounded hover:bg-white/60 ${c.sub}`}
          data-testid="bas-reminder-dismiss"
        >
          Dismiss
        </button>
      </div>
    </Card>
  );
}

export default function GstBoardTab({ BACKEND_URL }) {
  const today = new Date();
  const [view, setView] = useState('monthly');
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [week, setWeek] = useState(1);
  const [rangeStart, setRangeStart] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`);
  const [rangeEnd, setRangeEnd] = useState(today.toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    payment_date: today.toISOString().slice(0, 10),
    amount: '',
    notes: '',
    period_start: '',
    period_end: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ view });
      if (view === 'range') {
        params.append('start_date', rangeStart);
        params.append('end_date', rangeEnd);
      } else {
        params.append('year', year);
        if (view !== 'yearly') params.append('month', month);
        if (view === 'weekly') params.append('week', week);
      }
      if (view === 'daily') {
        // daily = single day range using rangeStart as the day
        params.set('view', 'range');
        params.set('start_date', rangeStart);
        params.set('end_date', rangeStart);
      }
      const res = await fetch(`${BACKEND_URL}/api/super-admin/gst/summary?${params}`,
        { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        setData(json);
      } else {
        toast.error('Failed to load GST summary');
      }
    } catch (e) {
      console.error('GST fetch failed:', e);
      toast.error('Failed to load GST summary');
    } finally { setLoading(false); }
  }, [BACKEND_URL, view, year, month, week, rangeStart, rangeEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const submitPayment = async () => {
    const amount = parseFloat(paymentForm.amount);
    if (!paymentForm.payment_date) return toast.error('Payment date required');
    if (!amount || amount <= 0) return toast.error('Amount must be > 0');
    try {
      const res = await fetch(`${BACKEND_URL}/api/super-admin/gst/payments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_date: paymentForm.payment_date,
          amount,
          notes: paymentForm.notes,
          period_start: paymentForm.period_start || undefined,
          period_end: paymentForm.period_end || undefined,
        }),
      });
      if (res.ok) {
        toast.success('GST payment recorded');
        setShowPaymentModal(false);
        setPaymentForm({
          payment_date: today.toISOString().slice(0, 10),
          amount: '', notes: '', period_start: '', period_end: '',
        });
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to record payment');
      }
    } catch (e) {
      toast.error('Failed to record payment');
    }
  };

  const deletePayment = async (paymentId) => {
    if (!window.confirm('Delete this GST payment? This cannot be undone.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/super-admin/gst/payments/${paymentId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) {
        toast.success('Payment deleted');
        fetchData();
      } else {
        toast.error('Failed to delete payment');
      }
    } catch {
      toast.error('Failed to delete payment');
    }
  };

  const exportBas = async () => {
    try {
      const params = new URLSearchParams({ view });
      if (view === 'range' || view === 'daily') {
        params.set('view', 'range');
        params.set('start_date', view === 'daily' ? rangeStart : rangeStart);
        params.set('end_date', view === 'daily' ? rangeStart : rangeEnd);
      } else {
        params.append('year', year);
        if (view !== 'yearly') params.append('month', month);
        if (view === 'weekly') params.append('week', week);
      }
      const res = await fetch(`${BACKEND_URL}/api/super-admin/gst/bas-export?${params}`,
        { credentials: 'include' });
      if (!res.ok) { toast.error('Export failed'); return; }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename=([^;]+)/);
      const filename = match ? match[1].trim().replace(/"/g, '') : 'BAS_Worksheet.csv';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('BAS worksheet downloaded');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const entriesWithData = useMemo(
    () => (data?.daily || []).filter(r => r.has_entry || r.income_total || r.expense_claimable || r.expense_parts),
    [data]
  );

  if (loading && !data) {
    return (
      <Card className="p-6"><div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
      </div></Card>
    );
  }
  if (!data) return null;

  const { totals } = data;
  const outstanding = data.outstanding;
  const owesMoney = outstanding > 0;   // positive = owe ATO
  const refundDue = outstanding < 0;   // negative = refund coming
  const settled = Math.abs(outstanding) < 0.01;

  // Progress bar: paid / net_owed
  const owedBase = Math.max(0, totals.gst_net_owed);
  const paidPct = owedBase > 0 ? Math.min(100, (data.total_paid_in_period / owedBase) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="gst-board-tab">
      <BasReminderBanner BACKEND_URL={BACKEND_URL} />

      {/* Header / Filters */}
      <Card className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              GST / Tax Board
            </h3>
            <p className="text-sm text-slate-500">
              Net GST owed = GST on sales − GST on parts. Record BAS payments to track outstanding.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={view} onValueChange={setView}>
              <SelectTrigger className="w-[130px]" data-testid="gst-view-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="range">Date Range</SelectItem>
              </SelectContent>
            </Select>
            {view === 'daily' && (
              <Input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} className="w-[160px]" data-testid="gst-daily-date" />
            )}
            {(view === 'weekly' || view === 'monthly') && (
              <>
                <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                  <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map((y) => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
                  <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTH_NAMES.map((m, i) => <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </>
            )}
            {view === 'weekly' && (
              <Select value={week.toString()} onValueChange={(v) => setWeek(parseInt(v))}>
                <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4, 5].map((w) => <SelectItem key={w} value={w.toString()}>Week {w}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {view === 'range' && (
              <>
                <Input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} className="w-[160px]" data-testid="gst-range-start" />
                <span className="text-slate-400">to</span>
                <Input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} className="w-[160px]" data-testid="gst-range-end" />
              </>
            )}
            <Button variant="outline" onClick={exportBas} className="gap-2" data-testid="gst-export-bas-btn">
              <Download className="h-4 w-4" /> Export BAS
            </Button>
            <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
              <DialogTrigger asChild>
                <Button data-testid="gst-record-payment-btn" className="gap-2">
                  <Plus className="h-4 w-4" /> Record Payment
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="gst-payment-modal">
                <DialogHeader>
                  <DialogTitle>Record GST / BAS Payment</DialogTitle>
                  <DialogDescription>
                    Enter the payment you made to the ATO. It will reduce your outstanding balance.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Payment Date</Label>
                    <Input type="date" value={paymentForm.payment_date}
                      onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                      data-testid="gst-payment-date" />
                  </div>
                  <div>
                    <Label>Amount Paid ($)</Label>
                    <Input type="number" step="0.01" min="0" placeholder="0.00" value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                      data-testid="gst-payment-amount" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>BAS Period Start <span className="text-slate-400 text-xs">(optional)</span></Label>
                      <Input type="date" value={paymentForm.period_start}
                        onChange={(e) => setPaymentForm({ ...paymentForm, period_start: e.target.value })} />
                    </div>
                    <div>
                      <Label>BAS Period End <span className="text-slate-400 text-xs">(optional)</span></Label>
                      <Input type="date" value={paymentForm.period_end}
                        onChange={(e) => setPaymentForm({ ...paymentForm, period_end: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea rows={2} placeholder="e.g. Q3 BAS, via ATO portal"
                      value={paymentForm.notes}
                      onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                      data-testid="gst-payment-notes" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowPaymentModal(false)}>Cancel</Button>
                  <Button onClick={submitPayment} data-testid="gst-payment-submit">Record</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="kpi-gst-sales">
            <p className="text-xs text-blue-600 uppercase tracking-wide flex items-center gap-1">
              <Landmark className="h-3 w-3" /> GST on Sales
            </p>
            <p className="text-xl font-bold text-blue-900 mt-1">{fmtCurrency(totals.gst_collected)}</p>
            <p className="text-[11px] text-blue-500 mt-1">From {fmtCurrency(totals.income_total)} income ÷ 11</p>
          </div>
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="kpi-gst-parts">
            <p className="text-xs text-amber-600 uppercase tracking-wide flex items-center gap-1">
              <Package className="h-3 w-3" /> GST on Purchases
            </p>
            <p className="text-xl font-bold text-amber-900 mt-1">{fmtCurrency(totals.gst_claimable)}</p>
            <p className="text-[11px] text-amber-500 mt-1">From {fmtCurrency(totals.expense_claimable || totals.expense_parts)} claimable ÷ 11</p>
          </div>
          <div className={`p-4 border rounded-lg ${totals.gst_net_owed >= 0 ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="kpi-gst-net">
            <p className={`text-xs uppercase tracking-wide flex items-center gap-1 ${totals.gst_net_owed >= 0 ? 'text-rose-600' : 'text-emerald-700'}`}>
              <Receipt className="h-3 w-3" /> Net GST
            </p>
            <p className={`text-xl font-bold mt-1 ${totals.gst_net_owed >= 0 ? 'text-rose-900' : 'text-emerald-900'}`}>
              {totals.gst_net_owed < 0 ? '−' : ''}{fmtCurrency(Math.abs(totals.gst_net_owed))}
            </p>
            <p className={`text-[11px] mt-1 ${totals.gst_net_owed >= 0 ? 'text-rose-500' : 'text-emerald-600'}`}>
              {totals.gst_net_owed >= 0 ? 'you owe ATO' : 'refund due'}
            </p>
          </div>
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg" data-testid="kpi-gst-paid">
            <p className="text-xs text-purple-600 uppercase tracking-wide flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> Paid in Period
            </p>
            <p className="text-xl font-bold text-purple-900 mt-1">{fmtCurrency(data.total_paid_in_period)}</p>
            <p className="text-[11px] text-purple-500 mt-1">{data.payments.length} BAS payment(s)</p>
          </div>
          <div
            className={`p-4 border rounded-lg ${settled ? 'bg-teal-50 border-teal-300' : owesMoney ? 'bg-red-50 border-red-300' : 'bg-emerald-50 border-emerald-300'}`}
            data-testid="kpi-gst-outstanding"
          >
            <p className={`text-xs uppercase tracking-wide flex items-center gap-1 ${owesMoney ? 'text-red-700' : settled ? 'text-teal-700' : 'text-emerald-700'}`}>
              {owesMoney ? <TrendingUp className="h-3 w-3" /> : refundDue ? <TrendingDown className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
              Outstanding
            </p>
            <p className={`text-xl font-bold mt-1 ${owesMoney ? 'text-red-900' : settled ? 'text-teal-900' : 'text-emerald-900'}`}>
              {refundDue ? '−' : ''}{fmtCurrency(Math.abs(outstanding))}
            </p>
            <p className={`text-[11px] mt-1 ${owesMoney ? 'text-red-600' : settled ? 'text-teal-600' : 'text-emerald-600'}`}>
              {settled ? 'Settled ✓' : owesMoney ? 'Still owed to ATO' : 'ATO owes you'}
            </p>
          </div>
        </div>

        {/* Progress bar: paid vs owed */}
        {totals.gst_net_owed > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-600">Payment progress for this period</span>
              <span className="font-semibold">{paidPct.toFixed(0)}%</span>
            </div>
            <div className="relative w-full h-3 rounded-full bg-slate-200 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${paidPct >= 100 ? 'bg-emerald-500' : 'bg-orange-500'}`}
                style={{ width: `${Math.min(100, paidPct)}%` }}
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              {fmtCurrency(data.total_paid_in_period)} paid of {fmtCurrency(totals.gst_net_owed)} owed
              {paidPct >= 100 && <span className="text-emerald-600 font-medium"> · fully settled 🎉</span>}
            </p>
          </div>
        )}
      </Card>

      {/* Daily breakdown */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Calendar className="h-4 w-4" /> Daily GST breakdown
          </h4>
          <p className="text-xs text-slate-500">{fmtDate(data.start_date)} → {fmtDate(data.end_date)}</p>
        </div>
        {entriesWithData.length === 0 ? (
          <div className="text-center py-10 text-slate-500 text-sm">
            No income or parts expenses recorded in this period. Add daily entries under the "Daily Entries" tab.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border-collapse" data-testid="gst-daily-table">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="border-b p-2 text-left">Date</th>
                  <th className="border-b p-2 text-right">Income (sales)</th>
                  <th className="border-b p-2 text-right" title="Parts + Entertainment + Others">Claimable expense</th>
                  <th className="border-b p-2 text-right text-blue-700">GST collected</th>
                  <th className="border-b p-2 text-right text-amber-700">GST claimable</th>
                  <th className="border-b p-2 text-right font-semibold">Net GST</th>
                </tr>
              </thead>
              <tbody>
                {entriesWithData.map((r) => (
                  <tr key={r.date} className="hover:bg-slate-50">
                    <td className="p-2">{fmtDate(r.date)}</td>
                    <td className="p-2 text-right text-slate-700">{fmtCurrency(r.income_total)}</td>
                    <td className="p-2 text-right text-slate-700" title={`Parts ${fmtCurrency(r.expense_parts)} · Entertainment ${fmtCurrency(r.expense_entertainment || 0)} · Others ${fmtCurrency(r.expense_others || 0)}`}>
                      {fmtCurrency(r.expense_claimable ?? r.expense_parts)}
                    </td>
                    <td className="p-2 text-right text-blue-700">{fmtCurrency(r.gst_collected)}</td>
                    <td className="p-2 text-right text-amber-700">{fmtCurrency(r.gst_claimable)}</td>
                    <td className={`p-2 text-right font-semibold ${r.gst_net >= 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                      {r.gst_net < 0 ? '−' : ''}{fmtCurrency(Math.abs(r.gst_net))}
                    </td>
                  </tr>
                ))}
                <tr className="bg-slate-100 font-semibold">
                  <td className="p-2">TOTAL</td>
                  <td className="p-2 text-right">{fmtCurrency(totals.income_total)}</td>
                  <td className="p-2 text-right">{fmtCurrency(totals.expense_claimable ?? totals.expense_parts)}</td>
                  <td className="p-2 text-right text-blue-900">{fmtCurrency(totals.gst_collected)}</td>
                  <td className="p-2 text-right text-amber-900">{fmtCurrency(totals.gst_claimable)}</td>
                  <td className={`p-2 text-right ${totals.gst_net_owed >= 0 ? 'text-rose-900' : 'text-emerald-900'}`}>
                    {totals.gst_net_owed < 0 ? '−' : ''}{fmtCurrency(Math.abs(totals.gst_net_owed))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Payments history */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Wallet className="h-4 w-4" /> BAS Payments recorded in this period
          </h4>
          <p className="text-xs text-slate-500">{data.payments.length} payment(s)</p>
        </div>
        {data.payments.length === 0 ? (
          <p className="text-center py-6 text-sm text-slate-500">
            No payments recorded yet. Click <strong>Record Payment</strong> above after you lodge your BAS.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="gst-payments-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="border-b p-2 text-left">Date</th>
                  <th className="border-b p-2 text-left">BAS Period</th>
                  <th className="border-b p-2 text-right">Amount</th>
                  <th className="border-b p-2 text-left">Notes</th>
                  <th className="border-b p-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.payments.map((p) => (
                  <tr key={p.payment_id} className="hover:bg-slate-50" data-testid={`gst-payment-row-${p.payment_id}`}>
                    <td className="p-2">{fmtDate(p.payment_date)}</td>
                    <td className="p-2 text-xs text-slate-600">
                      {p.period_start && p.period_end
                        ? `${fmtDate(p.period_start)} → ${fmtDate(p.period_end)}`
                        : '—'}
                    </td>
                    <td className="p-2 text-right font-semibold text-purple-800">{fmtCurrency(p.amount)}</td>
                    <td className="p-2 text-xs text-slate-600 max-w-xs truncate">{p.notes || '—'}</td>
                    <td className="p-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => deletePayment(p.payment_id)}
                        data-testid={`gst-payment-delete-${p.payment_id}`}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Footer formula */}
      <Card className="p-4 bg-slate-900 text-slate-100 font-mono text-[11px] leading-relaxed">
        <p>
          <span className="text-slate-400">Formula —</span>{' '}
          <span className="text-blue-300">gst_collected</span> = income ÷ 11;{' '}
          <span className="text-amber-300">gst_claimable</span> = (parts + entertainment + others) ÷ 11;{' '}
          <span>net = collected − claimable</span>;{' '}
          <span className="text-emerald-300">outstanding</span> = net − payments.
          <span className="text-slate-400"> Cash expenses excluded (typically wages / petty cash, no GST input credit).</span>
        </p>
      </Card>
    </div>
  );
}
